import useAction from '@/hooks/useAction';
import { handleError } from '@/lib/errors';
import { createStore } from 'cross-state';
import type { MaybePromise } from 'lib/models/maybePromise';
import { sleep } from 'radash';
import { useMemo, type ComponentType } from 'react';

export interface ActionButtonProps {
  actionId?: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => MaybePromise<unknown>;
  loading?: boolean;
  disabled?: boolean;
  concurrent?: boolean;
  minimumLoadingTime?: number;
}

interface ComponentProps {
  loading?: boolean;
  disabled?: boolean;
}

type ActionComponentProps<T> = ActionButtonProps & Omit<T, keyof ActionButtonProps | keyof ComponentProps>;

export const actions = createStore(new Set<unknown>());

export function runWithActionId(actionId: unknown, fn: () => MaybePromise<unknown>) {
  actions.add(actionId);

  try {
    const result = fn();

    if (result instanceof Promise) {
      return result.finally(() => {
        actions.delete(actionId);
      });
    } else {
      actions.delete(actionId);
    }

    return result;
  } catch (error) {
    actions.delete(actionId);
    throw error;
  }
}

export default function createActionButton<TProps extends ComponentProps>(
  Component: ComponentType<TProps>,
): ComponentType<ActionComponentProps<TProps>> {
  function ActionComponent(props: ActionComponentProps<TProps>) {
    const {
      actionId: propsActionId,
      onClick,
      loading,
      disabled,
      concurrent,
      minimumLoadingTime,
      ...componentProps
    } = props as Omit<TProps, 'actionId' | 'onClick'> & ActionButtonProps;

    const actionId = useMemo(() => propsActionId ?? Math.random(), [propsActionId]);

    const { actionInProgress, someActionInProgress } = actions.useStore((state) => {
      return {
        actionInProgress: state.has(actionId),
        someActionInProgress: state.size > 0,
      };
    });

    const { execute, inProgress } = useAction(async (e: React.MouseEvent<HTMLButtonElement>) => {
      if (actions.get().size > 0) {
        return;
      }

      actions.add(actionId);

      try {
        const promises = [Promise.resolve(onClick(e))];

        if (minimumLoadingTime) {
          promises.push(sleep(minimumLoadingTime));
        }

        await Promise.all(promises);
      } catch (error) {
        handleError(error);
      } finally {
        actions.delete(actionId);
      }
    });

    return (
      <Component
        {...({
          ...componentProps,
          onClick: execute,
          loading: loading ?? (inProgress || actionInProgress),
          disabled: disabled ?? loading ?? (!concurrent && someActionInProgress),
        } as any)}
      />
    );
  }

  if (Component.displayName) {
    ActionComponent.displayName = `Action(${Component.displayName})`;
  }

  return ActionComponent;
}
